/**
 * ADVANCED FILTERS — a sticky left rail at lg+, a bottom sheet below it.
 *
 * ── EVERY DIMENSION THE BACKEND SUPPORTS IS HERE ──
 *
 * Including the six the old screens never exposed and which cost no backend work
 * at all: the entry-kind multi-select, severity AS A FILTER (it was only ever a
 * breakdown dimension), the turnover bucket, the activity groups, the actor
 * filter and the time-of-day window. CONSOLIDATION_INVENTORY.md §9 is the list.
 *
 * ── A FIELD THE SELECTED BOOK CANNOT ANSWER IS NOT RENDERED ──
 *
 * Driven from `BOOK_FIELDS`, the same map the reducer strips with. This is what
 * makes the 2026-08-14 defect unrepeatable: the drawer used to offer a Building
 * Activities select on the Parcels and Residents books, the value fell through
 * to the server's JS equality pass, matched a column that does not exist on the
 * row, and the report came back EMPTY with a completely reasonable-looking
 * sidebar.
 *
 * ── AND THE RAIL IS NOT PORTALLED ──
 *
 * At lg+ it is an ordinary sticky column. Only the phone sheet portals. That is
 * deliberate: rsuite's date pickers portal their calendar to <body> at z-index
 * 1055, and a portalled scrim at 2000 puts the calendar underneath it — which is
 * how the reports filter drawer had to stay un-portalled for its whole life.
 * Keeping the desk case out of a portal removes the constraint instead of
 * inheriting it.
 */
import React from 'react';
import {
  ACTIVITY_GROUPS,
  BOOKS,
  BOOK_LABELS,
  Book,
  DashboardFilter,
  FilterMeta,
  PARCEL_STATUSES,
  bookCanFilter,
} from '../state/filter-model';
import { FilterAction } from '../state/filter-reducer';
import { useStaysProduct } from '../../../../../utils/app-features';
import {
  CheckRow,
  Field,
  Input,
  RailGroup,
  RailTitle,
  Select,
} from '../dashboard-screen.styled';

export interface FilterRailProps {
  filter: DashboardFilter;
  meta?: FilterMeta;
  dispatch: (action: FilterAction, opts?: { replace?: boolean }) => void;
  canReadAnalytics: boolean;
  /** The people who actually appear in this window, from the staff breakdown, so
   *  the list is never a roster of everybody. */
  actorOptions?: { id: string; label: string }[];
  /** Real units, so a filter can only produce a value that exists. Free text was
   *  compared with `===` on the server, so "10", "Unit 10" and "10 " were three
   *  different filters and two of them returned nothing — an empty report that
   *  looks exactly like a quiet month. */
  unitOptions?: { value: string; label: string; archived?: boolean }[];
}

const PRIORITIES = ['Low', 'Medium', 'High'];
// 'Short-Stay Guest' (Lobbero Stays plan S3) is appended so Records can show a
// stay's arrivals. 'Resident Entry' is still missing here (an existing gap, not
// changed by S3 — noted in PLAN-S3-LOG.md for the owner).
const VISITOR_TYPES = ['Guest', 'Prospect', 'Vendor', 'Delivery', 'Emergency (911)', 'Short-Stay Guest'];
const BUILDING_ACTIVITIES = [
  'Maintenance',
  'Leasing Office',
  'Resident',
  'Miscellaneous',
  'Shift Change',
];
const CARRIERS = ['Amazon', 'DHL', 'FedEx', 'UPS', 'USPS', 'Temu', 'Other'];
const PARCEL_STATUS_LABELS: Record<string, string> = {
  pending: 'On the shelf',
  picked_up: 'Collected',
  returned: 'Returned to sender',
};
const YES_NO = ['Yes', 'No'];

export const FilterRailFields: React.FC<FilterRailProps> = ({
  filter,
  meta,
  dispatch,
  canReadAnalytics,
  actorOptions = [],
  unitOptions = [],
}) => {
  const patch = (p: Partial<DashboardFilter>) => dispatch({ type: 'patch', patch: p });
  const can = (field: keyof DashboardFilter) => bookCanFilter(filter.book, field);
  // IS8.0 (IS8-D4): with Lobbero Stays switched off on the server, its visitor
  // type is not offered.
  const staysOn = useStaysProduct() === true;
  const visitorTypes = staysOn ? VISITOR_TYPES : VISITOR_TYPES.filter(v => v !== 'Short-Stay Guest');

  const text = (
    label: string,
    field: 'name' | 'lastName' | 'q',
    placeholder: string
  ) => (
    <Field key={field}>
      <span>{label}</span>
      <Input
        value={(filter[field] as string) || ''}
        placeholder={placeholder}
        aria-label={label}
        data-testid={`filter-${field}`}
        onChange={e => patch({ [field]: e.target.value || null } as Partial<DashboardFilter>)}
      />
    </Field>
  );

  const select = (
    label: string,
    field: keyof DashboardFilter,
    options: { value: string; label: string }[],
    anyLabel: string
  ) => (
    <Field key={String(field)}>
      <span>{label}</span>
      <Select
        value={(filter[field] as string) || ''}
        aria-label={label}
        data-testid={`filter-${String(field)}`}
        onChange={e =>
          patch({ [field]: e.target.value || null } as Partial<DashboardFilter>)
        }
      >
        <option value="">{anyLabel}</option>
        {options.map(option => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </Select>
    </Field>
  );

  return (
    <>
      {/* ── SCOPE ── */}
      <RailGroup>
        <RailTitle>Scope</RailTitle>
        {/* ONE PORTFOLIO IS NOT A CHOICE (ruling R-11 / BUG-023).
            Rendered only when there is more than one to choose between. With a
            single portfolio the control offers "All portfolios" and the one
            portfolio, which are the same set of buildings under two names — a
            decision with one outcome, taking up a row and inviting a click that
            changes nothing.

            The test is `> 1`, not `> 0`, and it is evaluated live: the moment a
            second portfolio exists this appears on its own, with no code change
            and no redeploy. It is also the CALLER's list — meta.portfolios is
            already scoped to what this user may see — so a manager who covers
            one portfolio inside a four-portfolio organisation correctly sees no
            picker either. */}
        {(meta?.portfolios.length || 0) > 1 ? (
          <Field>
            <span>Portfolio</span>
            <Select
              value={filter.portfolioId == null ? '' : String(filter.portfolioId)}
              aria-label="Portfolio"
              data-testid="filter-portfolio"
              onChange={e =>
                dispatch({
                  type: 'setScope',
                  scope: {
                    portfolioId: e.target.value ? Number(e.target.value) : null,
                  },
                })
              }
            >
              <option value="">All portfolios</option>
              {(meta?.portfolios || []).map(p => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </Select>
          </Field>
        ) : null}

        <Field>
          <span>Building</span>
          <Select
            value={filter.buildingId == null ? '' : String(filter.buildingId)}
            aria-label="Building"
            data-testid="filter-building"
            onChange={e =>
              dispatch({
                type: 'setScope',
                scope: { buildingId: e.target.value ? Number(e.target.value) : null },
              })
            }
          >
            <option value="">All buildings</option>
            {(meta?.buildings || []).map(b => (
              <option key={b.id} value={b.id}>
                {b.name}
              </option>
            ))}
          </Select>
        </Field>

        <Field>
          <span>Unit</span>
          {unitOptions.length ? (
            <Select
              value={filter.unit || ''}
              aria-label="Unit"
              data-testid="filter-unit"
              onChange={e => dispatch({ type: 'setScope', scope: { unit: e.target.value || null } })}
            >
              <option value="">Any unit</option>
              {unitOptions.map(option => (
                <option key={option.label} value={option.value}>
                  {option.label}
                  {option.archived ? ' (archived)' : ''}
                </option>
              ))}
            </Select>
          ) : (
            /* A select with one empty option is a filter that cannot be used —
               where a text box at least still works. The units list failing must
               not take the rest of the rail down with it. */
            <Input
              value={filter.unit || ''}
              placeholder="Any unit"
              aria-label="Unit"
              data-testid="filter-unit"
              onChange={e => dispatch({ type: 'setScope', scope: { unit: e.target.value || null } })}
            />
          )}
        </Field>
      </RailGroup>

      {/* ── TIME OF DAY ── */}
      <RailGroup>
        <RailTitle>Time of day</RailTitle>
        <Field>
          <span>From</span>
          <Input
            type="time"
            value={filter.timeFrom || ''}
            aria-label="From time"
            data-testid="filter-time-from"
            onChange={e => patch({ timeFrom: e.target.value || null })}
          />
        </Field>
        <Field>
          <span>To</span>
          <Input
            type="time"
            value={filter.timeTo || ''}
            aria-label="To time"
            data-testid="filter-time-to"
            onChange={e => patch({ timeTo: e.target.value || null })}
          />
        </Field>
      </RailGroup>

      {/* ── MEASURE AND SUBJECT (analytics only) ── */}
      {canReadAnalytics ? (
        <RailGroup>
          <RailTitle>Measure</RailTitle>
          <Field>
            <span>Counting</span>
            <Select
              value={filter.measure}
              aria-label="Measure"
              data-testid="filter-measure"
              onChange={e => dispatch({ type: 'setMeasure', measure: e.target.value })}
            >
              {(meta?.measures || []).map(m => (
                <option key={m.key} value={m.key}>
                  {m.label}
                </option>
              ))}
            </Select>
          </Field>
          <Field>
            <span>Dwell subject</span>
            <Select
              value={filter.subject}
              aria-label="Dwell subject"
              data-testid="filter-subject"
              onChange={e => patch({ subject: e.target.value })}
            >
              {(meta?.latencySubjects || []).map(s => (
                <option key={s} value={s}>
                  {s === 'parcel' ? 'Parcels' : s === 'visitor' ? 'Visits' : 'Activities'}
                </option>
              ))}
            </Select>
          </Field>
          <Field>
            <span>Turnover bucket</span>
            <Select
              value={filter.turnoverBucket}
              aria-label="Turnover bucket"
              data-testid="filter-turnover-bucket"
              onChange={e =>
                patch({ turnoverBucket: e.target.value as DashboardFilter['turnoverBucket'] })
              }
            >
              {(meta?.turnoverBuckets || []).map(b => (
                <option key={b} value={b}>
                  {b.charAt(0).toUpperCase() + b.slice(1)}
                </option>
              ))}
            </Select>
          </Field>
        </RailGroup>
      ) : null}

      {/* ── CLASSIFICATION ── */}
      <RailGroup>
        <RailTitle>Classification</RailTitle>

        {/* THE ENTRY-KIND MULTI-SELECT. Accepted by three routes and never
            offered by any screen until now. */}
        <Field as="div">
          <span>Kind</span>
          {(meta?.kinds || []).map(kind => (
            <CheckRow key={kind}>
              <input
                type="checkbox"
                checked={filter.kinds.includes(kind)}
                data-testid={`filter-kind-${kind}`}
                onChange={() =>
                  dispatch({ type: 'toggleMulti', field: 'kinds', value: kind })
                }
              />
              {kind}
            </CheckRow>
          ))}
        </Field>

        {/* SEVERITY AS A FILTER. It was only ever a breakdown dimension. */}
        {select(
          'Severity',
          'severity',
          (meta?.severity || []).map(s => ({
            value: s,
            label: s === 'follow_up' ? 'Follow up' : s === 'ok' ? 'OK' : 'Incident',
          })),
          'Any severity'
        )}

        {select(
          'Direction',
          'direction',
          [
            { value: 'in', label: 'In' },
            { value: 'out', label: 'Out' },
          ],
          'In and out'
        )}

        {select(
          'Status',
          'status',
          [
            { value: 'open', label: 'Still inside' },
            { value: 'closed', label: 'Departed' },
          ],
          'Any status'
        )}

        <Field as="div">
          <span>Activity group</span>
          {ACTIVITY_GROUPS.map(group => (
            <CheckRow key={group}>
              <input
                type="checkbox"
                checked={filter.groups.includes(group)}
                data-testid={`filter-group-${group}`}
                onChange={() =>
                  dispatch({ type: 'toggleMulti', field: 'groups', value: group })
                }
              />
              {group === 'daily_log' ? 'Building activities' : group.charAt(0).toUpperCase() + group.slice(1)}
            </CheckRow>
          ))}
        </Field>
      </RailGroup>

      {/* ── PEOPLE ── */}
      <RailGroup>
        <RailTitle>People</RailTitle>
        {/* WHO WROTE IT DOWN. The caveat is permanent and visible, because a
            per-person count is the most convincing thing on this screen that
            could be mistaken for a shift-hours report, and Lobbero has no such
            report. */}
        {actorOptions.length ? (
          <Field as="div">
            <span>Recorded by</span>
            {actorOptions.map(actor => (
              <CheckRow key={actor.id}>
                <input
                  type="checkbox"
                  checked={filter.actors.includes(actor.id)}
                  data-testid={`filter-actor-${actor.id}`}
                  onChange={() =>
                    dispatch({ type: 'toggleMulti', field: 'actors', value: actor.id })
                  }
                />
                {actor.label}
              </CheckRow>
            ))}
          </Field>
        ) : null}
        {can('name') ? text('First name', 'name', 'Any name') : null}
        {can('lastName') ? text('Last name', 'lastName', 'Any last name') : null}
      </RailGroup>

      {/* ── THE BOOK, AND ONLY THE FIELDS IT CAN ANSWER ── */}
      <RailGroup>
        <RailTitle>Records</RailTitle>
        <Field>
          <span>Book</span>
          <Select
            value={filter.book}
            aria-label="Book"
            data-testid="filter-book"
            onChange={e => dispatch({ type: 'setBook', book: e.target.value as Book })}
          >
            {BOOKS.map(book => (
              <option key={book} value={book}>
                {BOOK_LABELS[book]}
              </option>
            ))}
          </Select>
        </Field>

        {can('visitorType')
          ? select(
              'Visitor type',
              'visitorType',
              visitorTypes.map(v => ({ value: v, label: v })),
              'Any visitor type'
            )
          : null}
        {can('buildingActivity')
          ? select(
              'Building activity',
              'buildingActivity',
              BUILDING_ACTIVITIES.map(v => ({ value: v, label: v })),
              'Any activity'
            )
          : null}
        {can('priority')
          ? select(
              'Priority',
              'priority',
              PRIORITIES.map(v => ({ value: v, label: v })),
              'Any priority'
            )
          : null}
        {can('carrier')
          ? select(
              'Carrier',
              'carrier',
              CARRIERS.map(v => ({ value: v, label: v })),
              'Any carrier'
            )
          : null}
        {can('parcelStatus')
          ? select(
              'Parcel status',
              'parcelStatus',
              PARCEL_STATUSES.map(v => ({ value: v, label: PARCEL_STATUS_LABELS[v] })),
              'Any status'
            )
          : null}
        {can('approval')
          ? select(
              'Approval',
              'approval',
              YES_NO.map(v => ({ value: v, label: v })),
              'Any'
            )
          : null}
        {can('parkingValidation')
          ? select(
              'Parking validated',
              'parkingValidation',
              YES_NO.map(v => ({ value: v, label: v })),
              'Any'
            )
          : null}
        {can('preAuthorized')
          ? select(
              'Pre-authorised',
              'preAuthorized',
              YES_NO.map(v => ({ value: v, label: v })),
              'Any'
            )
          : null}
        {/* Resident-portal Plan 5: arrivals that came on a visitor pass. */}
        {can('onPass')
          ? select(
              'On pass',
              'onPass',
              YES_NO.map(v => ({ value: v, label: v })),
              'Any'
            )
          : null}
        {can('numberOfGuests') ? (
          <Field>
            <span>Number of guests</span>
            <Select
              value={filter.numberOfGuests == null ? '' : String(filter.numberOfGuests)}
              aria-label="Number of guests"
              data-testid="filter-numberOfGuests"
              onChange={e =>
                patch({ numberOfGuests: e.target.value ? Number(e.target.value) : null })
              }
            >
              <option value="">Any number</option>
              {Array.from({ length: 20 }, (_, i) => i + 1).map(n => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </Select>
          </Field>
        ) : null}
      </RailGroup>

      {/* ── FREE TEXT ── */}
      <RailGroup>
        <RailTitle>Search</RailTitle>
        {text('Search everything', 'q', 'Any word')}
      </RailGroup>
    </>
  );
};
